import { ModalError, ModalLoading } from '@clearcover/atlas';
import {
  AdasStandardGroupsType,
  AdasStandardGroupType,
  AddressType,
  CoverageFormOptionType,
  CoveredPartyPayloadType,
  CreateOrUpdateVehicleResponseType,
  DisplayCoverageType,
  DriverType,
  VehicleAggregatesV2$GetYearsResponse,
  VehicleOptionsResponse,
  VehicleType,
} from '@clearcover/core-app-types';
import { yupZipShortValidator } from 'Admin/utils/formValidators';
import { Formik, FormikHelpers } from 'formik';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import { areSameAddress } from 'Policies/utils/helpers';
import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  FormModalContent,
  FormModalFooter,
  FormModalHeader,
  FormModalUnderWritingErrors,
} from 'Shared/components';
import { env } from 'Shared/constants/env';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { useQaToggleFeature } from 'Shared/hooks';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useApi, useLazyApi, useLazyPortalV2Api } from 'Shared/services/Api';
import * as Yup from 'yup';
import {
  VehicleAddress,
  VehicleCoverage,
  VehicleDetails,
  VehicleDriverAssignment,
  VehicleInfo,
} from '.';
import { AdvancedDriverAssistanceSystems } from './AdvancedDriverAssistanceSystems';
import { buildCreateOrUpdateVehiclePayload } from './utils';

type LienholderType = {
  name: string;
  zip: string;
  state: string;
  city: string;
  address1: string;
  address2: string;
};

export type AddVehicleFormValues = {
  identifier: string;
  type: string;

  // info:
  vin: string;
  year: string;
  make: string;
  model: string;
  body: string;
  alarmsystem: boolean;
  passivedisable: boolean;
  activedisable: boolean;
  vehiclerecovery: boolean;

  // details
  avgMiles: string;
  rideShare: string;
  primaryUse: string;
  ownershipStatus: string;
  lienholder: LienholderType;
  coverages: {
    [coverageIdentifier: string]: string | boolean;
  };

  usageDetails: {
    usedInMexico: boolean | null;
    availableForNonCoveredDrivers: boolean | null;
    emergencyServices: boolean | null;
    feeBasedServices: boolean | null;
    hazmatToolTransport: boolean | null;
    usedAsResidence: boolean | null;
    noneOfTheAbove: boolean | null;
  };

  // Garaging Address
  parkedAtMailing: boolean;
  garagingAddress: AddressType;
  parkingLocationReason: string | null;
  inStateParkingDuration: string | null;
  parkingType: string | null;

  // driver assignment
  assignedDrivers: Array<DriverType['id']>;

  // Advanced Driver-Assistance Systems
  adas: {
    passive: AdasStandardGroupType;
    active: AdasStandardGroupType;
    parkingFeedback: AdasStandardGroupType;
    drivingFeedback: AdasStandardGroupType;
  };
  adasReportRan: boolean;
  registeredToPolicyholderOrSpouse?: boolean | null;

  hasAntiTheftDevice?: boolean | null;
  unacceptableBusinessUse?: boolean | null;
};
const LEINHOLDER_VALIDATION = {
  is: (val: string) => val !== 'owned',
  then: Yup.object().shape({
    address1: Yup.string().required('Street address is required'),
    address2: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string()
      .oneOf(Object.keys(STATE_OPTIONS))
      .required('State is required'),
    zip: yupZipShortValidator().required('ZIP Code is required'),
  }),
  otherwise: Yup.object().notRequired(),
};

const VEHICLE_SCHEMA = {
  // info:
  vin: Yup.string().when(['year', 'make', 'model', 'body'], {
    is: (...args) => !args.every(val => !!val),
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired(),
  }),
  year: Yup.string().required('Required'),
  make: Yup.string().required('Required'),
  model: Yup.string().required('Required'),
  body: Yup.string().required('Required'),
  alarmsystem: Yup.boolean().notRequired(),
  passivedisable: Yup.boolean().notRequired(),
  activedisable: Yup.boolean().notRequired(),
  vehiclerecovery: Yup.boolean().notRequired(),

  // details
  primaryUse: Yup.string().required('Required'),
  ownershipStatus: Yup.string().required('Required'),
  lienholder: Yup.object().when('ownershipStatus', LEINHOLDER_VALIDATION),

  // assigned drivers
  assignedDrivers: Yup.array()
    .ensure()
    .of(Yup.string())
    .compact()
    .min(1, 'Each vehicle must be assigned to at least one driver')
    .required('Required'),
};

const ADAS_GROUP_VALIDATION = Yup.object()
  .shape({
    equipped: Yup.string().oneOf(['yes', 'no']),
    source: Yup.string(),
  })
  .required();

export type VehicleFormType = {
  close: () => void;
  type: 'create' | 'update';
  vehicle?: VehicleType;
  drivers: Array<DriverType>;
  mailingAddress: AddressType;
  isNSA: boolean;
};

export const transformOptions = (
  options: Array<{ identifier: string; label: string }>
): {
  [key: string]: string;
} => {
  return options.reduce((acc, { identifier, label }) => {
    return {
      ...acc,
      [identifier]: label,
    };
  }, {});
};

function isMailingAddressValidation(mailingAddress: AddressType) {
  return function(this: Yup.TestContext, value: any) {
    return areSameAddress(value, mailingAddress)
      ? this.createError({
          path: 'garagingAddress.address1',
          message:
            'Garaging address and mailing address match. Please update garaging address',
        })
      : true;
  };
}

export const VehicleForm: FC<VehicleFormType> = ({
  close,
  type,
  vehicle,
  drivers,
  mailingAddress,
  isNSA,
}) => {
  const history = useHistory();
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const {
    details,
    flags,
    refetchHandler,
    resourceEffectiveGrants,
  } = useContext(PolicyContext);
  const {
    underwritingErrors,
    setUnderwritingErrors,
    hasUnderwritingErrors,
  } = useUnderwritingErrors();

  const [{ data, loading: isLoading, error }] = useApi<VehicleOptionsResponse>(
    `/options/vehicle?identifier=${details.filingIdentifier}&region=${details.region}`
  );

  const [, createOrUpdateVehicle] = useLazyApi<
    CreateOrUpdateVehicleResponseType
  >({
    method: 'POST',
    url: `/policies/${policyId}/createOrUpdateVehicle${draftEndpointModifier(
      details.draftType,
      flags.renewalQuotesEnabled
    )}`,
  });

  const [adasFeatureFlag] = useQaToggleFeature('adas_features');
  const [{ loading: loadingAdasReportData }, fetchAdasReportData] = useLazyApi<
    AdasStandardGroupsType
  >({ url: `policies/${policyId}/vehicle/adas`, method: 'POST' });

  const [
    {
      data: vehicleYearsData,
      loading: vehicleYearsLoading,
      error: vehicleYearsError,
    },
    fetchVehicleYears,
  ] = useLazyPortalV2Api<VehicleAggregatesV2$GetYearsResponse>({
    url: '/vehicles/years',
  });

  useEffect(() => {
    fetchVehicleYears({
      params: { configDiscoveryParams: details.configDiscoveryParams },
    }).catch(() => {});
  }, [fetchVehicleYears, details.configDiscoveryParams]);

  const vehicleYears = useMemo(() => {
    if (!vehicleYearsData?.years) return undefined;
    return Object.fromEntries(
      vehicleYearsData.years.map((year: number) => [
        year.toString(),
        year.toString(),
      ])
    );
  }, [vehicleYearsData]);

  const getAdasReportData = useCallback(
    (values: AddVehicleFormValues) => () =>
      fetchAdasReportData(
        buildCreateOrUpdateVehiclePayload({
          values,
          type,
          data,
          requiredAccessStatements: details.requiredAccessStatements ?? false,
        })
      ),
    [data, fetchAdasReportData, type, details.requiredAccessStatements]
  );

  if (isLoading || vehicleYearsLoading) {
    return <ModalLoading data-testid="vehicle-modal-loading" />;
  }

  const {
    askForAntiTheftDevice,
    askForBusinessUseType,
    askForAvgMilesPerYear,
    askForRegisteredToPolicyholderOrSpouse,
  } = data?.vehicleOptionsQuestionsConfig ?? {};

  const initializationError =
    error ||
    vehicleYearsError ||
    (!data.coverages && new Error('Could not load form data'));

  if (initializationError) {
    return (
      <ModalError
        data-testid="add-vehicle-error"
        errorMessage={initializationError.message}
        componentStack={initializationError.stack}
      />
    );
  }

  const parkingLocationReasons = transformOptions(data.parkingLocationReasons);

  const inStateParkingDurations = transformOptions(
    data.inStateParkingDurations
  );

  const parkingTypes = transformOptions(data.parkingTypes);

  const createInitialCoverages = () => {
    if (type === 'create') {
      return {
        ...buildCoveragesFromOptions(data?.coverages ?? []),
        ...buildCoveragesFromOptions(data?.endorsements ?? []),
      };
    } else {
      return {
        ...buildCoveragesFromExistingVehicle(vehicle?.coverages ?? []),
        ...buildCoveragesFromExistingVehicle(vehicle?.endorsements ?? []),
      };
    }
  };

  const buildCoveragesFromOptions = (coverageArray: CoverageFormOptionType[]) =>
    coverageArray.reduce((acc, { identifier, recommendation }) => {
      return {
        ...acc,
        [`${identifier}`]: recommendation ?? '',
      };
    }, {});

  const buildCoveragesFromExistingVehicle = (
    coverageArray: DisplayCoverageType[]
  ) =>
    coverageArray.reduce((acc, { coverageIdentifier, value }) => {
      return {
        ...acc,
        [`${coverageIdentifier}`]: value ?? '',
      };
    }, {});

  const buildCoverageSchema = () => {
    if (type === 'create') {
      return {
        ...buildCoverageSchemaFromOptions(data?.coverages ?? []),
        ...buildCoverageSchemaFromOptions(data?.endorsements ?? []),
      };
    } else {
      return {
        ...buildCoverageSchemaFromExistingVehicle(vehicle?.coverages ?? []),
        ...buildCoverageSchemaFromExistingVehicle(vehicle?.endorsements ?? []),
      };
    }
  };

  const buildCoverageSchemaFromOptions = (
    coverageArray: CoverageFormOptionType[]
  ) =>
    coverageArray.reduce(
      (acc, { identifier }) => ({
        ...acc,
        [`${identifier}`]: Yup.string().required('Required'),
      }),
      {}
    );

  const buildCoverageSchemaFromExistingVehicle = (
    coverageArray: DisplayCoverageType[]
  ) =>
    coverageArray.reduce(
      (acc, { coverageIdentifier }) => ({
        ...acc,
        [`${coverageIdentifier}`]: Yup.string().required('Required'),
      }),
      {}
    );

  const noneOfTheAboveInitialValue = (
    usageDetails:
      | {
          usedInMexico?: boolean | null;
          availableForNonCoveredDrivers?: boolean | null;
          emergencyServices?: boolean | null;
          feeBasedServices?: boolean | null;
          hazmatToolTransport?: boolean | null;
          usedAsResidence?: boolean | null;
        }
      | undefined
  ) => {
    // If usageDetails is undefined, return false
    // If usageDetails is defined but is empty, return false
    // If usageDetails is defined and every value is false, return true
    return usageDetails && Object.values(usageDetails).length > 1
      ? Object.values(usageDetails).every(value => value === false)
      : false;
  };

  const INITIAL_FORM_VALUES: AddVehicleFormValues = {
    identifier: vehicle?.identifier ?? '',
    type,
    vin: vehicle?.vin ?? '',
    year: vehicle?.year ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    body: vehicle?.bodyStyle ?? '',
    alarmsystem: vehicle?.antiTheft.alarm ?? false,
    passivedisable: vehicle?.antiTheft.passiveDisable ?? false,
    activedisable: vehicle?.antiTheft.activeDisable ?? false,
    vehiclerecovery: vehicle?.antiTheft.recovery ?? false,
    avgMiles: vehicle?.usage.yearlyMileage?.toString() ?? '',
    rideShare: vehicle ? (vehicle.usage.rideShare ? 'yes' : 'no') : '',
    primaryUse: vehicle?.usage.use ?? '',
    ownershipStatus: vehicle?.ownerShipType ?? '',
    lienholder: {
      name: vehicle?.lienholder.name ?? '',
      address1: vehicle?.lienholder.addressLine1 ?? '',
      address2: vehicle?.lienholder.addressLine2 ?? '',
      city: vehicle?.lienholder.city ?? '',
      state: vehicle?.lienholder.state ?? '',
      zip: vehicle?.lienholder.postalCode ?? '',
    },
    garagingAddress: vehicle?.garagingAddress ?? mailingAddress,
    parkedAtMailing: vehicle?.garagingAddress
      ? areSameAddress(vehicle.garagingAddress, mailingAddress)
      : true,
    usageDetails: {
      usedInMexico: vehicle?.usageDetails?.usedInMexico ?? null,
      availableForNonCoveredDrivers:
        vehicle?.usageDetails?.availableForNonCoveredDrivers ?? null,
      emergencyServices: vehicle?.usageDetails?.emergencyServices ?? null,
      feeBasedServices: vehicle?.usageDetails?.feeBasedServices ?? null,
      hazmatToolTransport: vehicle?.usageDetails?.hazmatToolTransport ?? null,
      usedAsResidence: vehicle?.usageDetails?.usedAsResidence ?? null,
      noneOfTheAbove: noneOfTheAboveInitialValue(vehicle?.usageDetails),
    },
    parkingLocationReason: vehicle?.parkingLocationReason ?? null,
    inStateParkingDuration: vehicle?.inStateParkingDuration ?? null,
    parkingType: vehicle?.parkingType ?? null,
    adas: {
      passive: {
        equipped: 'unknown',
        source: 'userReported',
      },
      active: {
        equipped: 'unknown',
        source: 'userReported',
      },
      parkingFeedback: {
        equipped: 'unknown',
        source: 'userReported',
      },
      drivingFeedback: {
        equipped: 'unknown',
        source: 'userReported',
      },
    },
    adasReportRan: false,
    coverages: createInitialCoverages(),
    assignedDrivers: vehicle?.assignedDrivers ?? drivers.map(d => d.id),
    registeredToPolicyholderOrSpouse: vehicle?.registeredToPolicyholderOrSpouse,
    hasAntiTheftDevice: vehicle?.antiTheft.hasAntiTheftDevice,
    unacceptableBusinessUse:
      vehicle?.usageDetails?.unacceptableBusinessUse ?? null,
  };

  const VEHICLE_USAGE_VALIDATION = Yup.object()
    .shape({
      usedInMexico: Yup.boolean().nullable(),
      availableForNonCoveredDrivers: Yup.boolean().nullable(),
      emergencyServices: Yup.boolean().nullable(),
      feeBasedServices: Yup.boolean().nullable(),
      hazmatToolTransport: Yup.boolean().nullable(),
      usedAsResidence: Yup.boolean().nullable(),
      noneOfTheAbove: Yup.boolean().nullable(),
    })
    .test('at-least-one', 'At least one option must be selected', value => {
      // Check if at least one of the properties is true
      return (
        Object.values(value || {}).some(val => val === true) ||
        !(
          data.vehicleOptionsQuestionsConfig.askForUsageDetails &&
          env.vehicleNewQuestionsEnabled
        )
      );
    });

  const VEHICLE_DRIVER_ASSIGNMENTS_VALIDATION = {
    is: () => details.region === 'US-VA',
    then: Yup.array()
      .ensure()
      .of(Yup.string().required())
      .compact()
      .min(1, 'Each driver must be assigned to at least one vehicle')
      .required('Required'),
    otherwise: Yup.array().notRequired(),
  };

  const rideshareOffered =
    details.region !== 'US-OR' &&
    details.region !== 'US-FL' &&
    (isNSA ||
      data.endorsements
        .map(endorsement => endorsement.identifier)
        .includes('rideshare'));

  const vehicleValidationSchema = (mailingAddress: AddressType) =>
    Yup.object().shape({
      ...VEHICLE_SCHEMA,
      avgMiles: Yup.string().when('askForAvgMilesPerYear', {
        is: () => askForAvgMilesPerYear,
        then: Yup.string().required('Required'),
        otherwise: Yup.string().notRequired(),
      }),
      registeredToPolicyholderOrSpouse: Yup.boolean().when(
        'askForRegisteredToPolicyholderOrSpouse',
        {
          is: () => askForRegisteredToPolicyholderOrSpouse,
          then: Yup.boolean()
            .required('Required')
            .nullable(),
          otherwise: Yup.boolean()
            .notRequired()
            .nullable(),
        }
      ),
      hasAntiTheftDevice: Yup.boolean().when('askForAntiTheftDevice', {
        is: () => askForAntiTheftDevice,
        then: Yup.boolean().required('Required'),
        otherwise: Yup.boolean().notRequired(),
      }),
      unacceptableBusinessUse: Yup.boolean().when('primaryUse', {
        is: primaryUse => askForBusinessUseType && primaryUse === 'business',
        then: Yup.boolean()
          .required('Required')
          .nullable(),
        otherwise: Yup.boolean()
          .notRequired()
          .nullable(),
      }),
      garagingAddress: Yup.object()
        .shape({
          address1: Yup.string().required('Street address is required'),
          address2: Yup.string(),
          city: Yup.string().required('City is required'),
          state: Yup.string()
            .oneOf(Object.keys(STATE_OPTIONS))
            .required('State is required'),
          zip: yupZipShortValidator().required('ZIP Code is required'),
        })
        .when('parkedAtMailing', {
          is: parkedAtMailing => !parkedAtMailing,
          then: Yup.object().test({
            name: 'is-same-address',
            test: isMailingAddressValidation(mailingAddress),
          }),
        }),
      rideShare: Yup.string().when('rideshare', {
        is: () => rideshareOffered,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      coverages: Yup.object(buildCoverageSchema),
      assignedDrivers: Yup.array().when(
        'is-assignable',
        VEHICLE_DRIVER_ASSIGNMENTS_VALIDATION
      ),
      adas: Yup.object().when('showAdas', {
        is: () => adasFeatureFlag && data.showAdasInfo && type !== 'update',
        then: Yup.object()
          .shape({
            passive: Yup.object()
              .shape({
                equipped: Yup.string().oneOf(['yes', 'no', 'unknown']),
                source: Yup.string(),
              })
              .required(),
            active: ADAS_GROUP_VALIDATION,
            parkingFeedback: ADAS_GROUP_VALIDATION,
            drivingFeedback: ADAS_GROUP_VALIDATION,
          })
          .required(),
        otherwise: Yup.object(),
      }),
      adasReportRan: Yup.boolean().when('hasAdas', {
        is: () => adasFeatureFlag && data.showAdasInfo && type !== 'update',
        then: Yup.boolean()
          .oneOf([true])
          .required('Adas Report must be ran.'),
        otherwise: Yup.boolean(),
      }),
      usageDetails: VEHICLE_USAGE_VALIDATION,
      parkedAtMailing: Yup.boolean(),
      parkingLocationReason: Yup.string()
        .nullable()
        .when('parkedAtMailing', {
          is: parkedAtMailing =>
            parkedAtMailing === false &&
            data.vehicleOptionsQuestionsConfig.askForParkingLocationReason &&
            env.vehicleNewQuestionsEnabled &&
            data.showGaragingAddress,
          then: Yup.string()
            .nullable()
            .required('Required'),
          otherwise: Yup.string()
            .nullable()
            .notRequired(),
        }),
      inStateParkingDuration: Yup.string()
        .nullable()
        .when([], {
          is: () =>
            data.vehicleOptionsQuestionsConfig.askForInstateParkingDuration &&
            env.vehicleNewQuestionsEnabled &&
            data.showGaragingAddress,
          then: Yup.string()
            .oneOf(Object.keys(inStateParkingDurations))
            .required('Required'),
        }),
      parkingType: Yup.string()
        .nullable()
        .when([], {
          is: () =>
            data.vehicleOptionsQuestionsConfig.askForParkingType &&
            env.vehicleNewQuestionsEnabled &&
            data.showGaragingAddress,
          then: Yup.string().required('Required'),
        }),
    });

  const submitVehicleForm = async ({
    values,
    setStatus,
    setSubmitting,
    bypassUnderwriting,
  }: {
    values: CoveredPartyPayloadType;
    setStatus: (arg0: any) => void;
    setSubmitting: (arg0: boolean) => void;
    bypassUnderwriting?: boolean;
  }) => {
    const payload = buildCreateOrUpdateVehiclePayload({
      values,
      type,
      data,
      requiredAccessStatements: details.requiredAccessStatements ?? false,
      bypassUnderwriting,
    });
    return createOrUpdateVehicle(payload)
      .then(draft => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(
              `/policies/${policyId}/draft/${draft.data.draftId}`,
              {}
            );
      })
      .catch(status => {
        setUnderwritingErrors(status);
        setStatus(hasUnderwritingErrors(status) ? '' : status.message);
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      onSubmit={async (
        values,
        { setStatus, setSubmitting }: FormikHelpers<AddVehicleFormValues>
      ) =>
        submitVehicleForm({
          values,
          setStatus,
          setSubmitting,
          bypassUnderwriting: undefined,
        })
      }
      validationSchema={() => vehicleValidationSchema(mailingAddress)}
      validateOnMount
    >
      {({ values, setStatus, setSubmitting }) => {
        return (
          <>
            <FormModalHeader>
              {vehicle ? 'Edit Vehicle' : 'Add Vehicle'}
            </FormModalHeader>
            <FormModalContent>
              <VehicleInfo data={data} vehicleYears={vehicleYears} />
              <VehicleDetails data={data} rideshareOffered={rideshareOffered} />
              {data.showGaragingAddress && (
                <VehicleAddress
                  mailingAddress={mailingAddress}
                  parkingLocationReasons={parkingLocationReasons}
                  parkingTypes={parkingTypes}
                  inStateParkingDurations={inStateParkingDurations}
                  vehicle={vehicle}
                  vehicleOptionsQuestionsConfig={
                    data.vehicleOptionsQuestionsConfig
                  }
                />
              )}
              {data.showAdasInfo && type === 'create' && adasFeatureFlag && (
                <AdvancedDriverAssistanceSystems
                  type={type}
                  adasLabels={data.adasLabels || []}
                  adasReportFunc={{
                    loading: loadingAdasReportData,
                    getAdasReportData: getAdasReportData(values),
                  }}
                />
              )}
              {details.region === 'US-VA' ? (
                <VehicleDriverAssignment drivers={drivers} />
              ) : null}
              {type === 'create' && (
                <VehicleCoverage
                  coverages={data.coverages}
                  endorsements={data.endorsements}
                />
              )}
            </FormModalContent>
            <FormModalUnderWritingErrors
              errors={underwritingErrors}
              grants={resourceEffectiveGrants}
              submitIgnoringUnderwriting={() =>
                submitVehicleForm({
                  values,
                  setStatus,
                  setSubmitting,
                  bypassUnderwriting: true,
                })
              }
            />
            <FormModalFooter
              identifier={
                vehicle?.identifier
                  ? `${vehicle?.identifier}-edit-vehicle-form`
                  : 'add-vehicle-form'
              }
              close={close}
            />
          </>
        );
      }}
    </Formik>
  );
};
